import React, { useState, useEffect } from 'react';
import { Plus, Edit, Trash2, FileText } from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogTrigger } from '../ui/dialog';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../ui/table';
import { toast } from 'sonner';
import { certificationsAPI } from '../../services/api';
import { ImageUpload } from '../ui/image-upload';
import { RichTextEditor } from '../ui/rich-text-editor';

const CertificationsTab = () => {
  const [certifications, setCertifications] = useState([]);
  const [loading, setLoading] = useState(true);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [editingCert, setEditingCert] = useState(null);
  const [certForm, setCertForm] = useState({ name: '', description: '', icon: '', pdf_url: '' });

  useEffect(() => {
    loadCertifications();
  }, []);

  const loadCertifications = async () => {
    try {
      const response = await certificationsAPI.getAll();
      setCertifications(response.data);
    } catch (error) {
      toast.error('Errore nel caricamento delle certificazioni');
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingCert) {
        await certificationsAPI.update(editingCert.id, certForm);
        toast.success('Certificazione aggiornata!');
      } else {
        await certificationsAPI.create(certForm);
        toast.success('Certificazione aggiunta!');
      }
      loadCertifications();
      setIsDialogOpen(false);
      setEditingCert(null);
      setCertForm({ name: '', description: '', icon: '', pdf_url: '' });
    } catch (error) {
      toast.error('Errore nel salvare la certificazione');
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Sei sicuro di voler eliminare questa certificazione?')) return;
    try {
      await certificationsAPI.delete(id);
      toast.success('Certificazione eliminata!');
      loadCertifications();
    } catch (error) {
      toast.error('Errore nell\'eliminare la certificazione');
    }
  };

  const handleEdit = (cert) => {
    setEditingCert(cert);
    setCertForm({
      name: cert.name,
      description: cert.description,
      icon: cert.icon,
      pdf_url: cert.pdf_url || ''
    });
    setIsDialogOpen(true);
  };

  return (
    <Card className="glass-card">
      <CardHeader>
        <div className="flex flex-col sm:flex-row sm:items-center justify-between space-y-4 sm:space-y-0">
          <div>
            <CardTitle className="text-2xl">Gestione Certificazioni</CardTitle>
            <CardDescription>Aggiungi, modifica o elimina certificazioni</CardDescription>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
            <DialogTrigger asChild>
              <Button onClick={() => { setEditingCert(null); setCertForm({ name: '', description: '', icon: '', pdf_url: '' }); }}>
                <Plus className="w-4 h-4 mr-2" />Aggiungi Certificazione
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
              <DialogHeader>
                <DialogTitle>{editingCert ? 'Modifica Certificazione' : 'Nuova Certificazione'}</DialogTitle>
                <DialogDescription>{editingCert ? 'Aggiorna le informazioni' : 'Compila i dettagli'}</DialogDescription>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="name">Nome Certificazione</Label>
                  <Input id="name" value={certForm.name} onChange={(e) => setCertForm({ ...certForm, name: e.target.value })} required className="mt-2" placeholder="ISO 9001:2015" />
                </div>
                <div>
                  <Label htmlFor="description">Descrizione</Label>
                  <div className="mt-2">
                    <RichTextEditor
                      value={certForm.description}
                      onChange={(value) => setCertForm({ ...certForm, description: value })}
                      placeholder="Descrizione della certificazione..."
                      minHeight="100px"
                    />
                  </div>
                </div>
                <div>
                  <Label htmlFor="icon">Icona (Lucide React)</Label>
                  <Input id="icon" value={certForm.icon} onChange={(e) => setCertForm({ ...certForm, icon: e.target.value })} required className="mt-2" placeholder="Award" />
                  <p className="text-xs text-muted-foreground mt-1">Es: Award, Shield, CheckCircle</p>
                </div>
                <div>
                  <Label>Documento PDF (opzionale)</Label>
                  <div className="mt-2">
                    <ImageUpload
                      value={certForm.pdf_url}
                      onChange={(url) => setCertForm({ ...certForm, pdf_url: url })}
                      uploadType="document"
                      label="Carica documento PDF"
                    />
                  </div>
                </div>
                <div className="flex justify-end space-x-3 pt-4">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)}>Annulla</Button>
                  <Button type="submit">{editingCert ? 'Aggiorna' : 'Crea'} Certificazione</Button>
                </div>
              </form>
            </DialogContent>
          </Dialog>
        </div>
      </CardHeader>
      <CardContent>
        <div className="rounded-lg border border-border overflow-hidden">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Nome</TableHead>
                <TableHead>Descrizione</TableHead>
                <TableHead>Icona</TableHead>
                <TableHead>PDF</TableHead>
                <TableHead className="text-right">Azioni</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8">Caricamento...</TableCell></TableRow>
              ) : certifications.length === 0 ? (
                <TableRow><TableCell colSpan={5} className="text-center py-8 text-muted-foreground">Nessuna certificazione</TableCell></TableRow>
              ) : (
                certifications.map((cert) => (
                  <TableRow key={cert.id}>
                    <TableCell className="font-medium">{cert.name}</TableCell>
                    <TableCell>{cert.description}</TableCell>
                    <TableCell>{cert.icon}</TableCell>
                    <TableCell>
                      {cert.pdf_url ? (
                        <a href={cert.pdf_url} target="_blank" rel="noopener noreferrer" className="flex items-center text-primary hover:underline">
                          <FileText className="w-4 h-4 mr-1" />
                          PDF
                        </a>
                      ) : (
                        <span className="text-muted-foreground">-</span>
                      )}
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end space-x-2">
                        <Button variant="ghost" size="icon" onClick={() => handleEdit(cert)}><Edit className="w-4 h-4" /></Button>
                        <Button variant="ghost" size="icon" onClick={() => handleDelete(cert.id)} className="text-destructive hover:text-destructive"><Trash2 className="w-4 h-4" /></Button>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </div>
      </CardContent>
    </Card>
  );
};

export default CertificationsTab;